// The person's labels (one list per person, shared by Cases and People).
//
// Promise: every change shows at once and is rolled back if the server refuses
// it; other windows follow through `label.changed` / `label.removed`.
import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { useApi } from "../app/context";
import { type Api, idempotencyKeyFor } from "../data/api";
import { newOptimisticId, optimisticMutation } from "../data/optimistic";

export interface Label {
  id: string;
  name: string;
  /** `#rrggbb`. */
  color: string;
  position: number;
}

/**
 * The colors offered (the server hands them out in this order to a label made
 * without one). Mid tones: each reads as a dot and as text on light and dark.
 */
export const LABEL_COLORS = [
  "#ef4444",
  "#f97316",
  "#ca8a04",
  "#22c55e",
  "#14b8a6",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
] as const;

/** The longest name the server takes. */
export const LABEL_NAME_MAX = 50;

export const labelsKey = ["me", "labels"] as const;

export const labelsQuery = (api: Api) =>
  queryOptions({
    queryKey: labelsKey,
    queryFn: async () =>
      (await api<{ items: Label[] }>("/api/me/labels")).items,
  });

export function useLabels() {
  return useQuery(labelsQuery(useApi()));
}

export const invalidateLabels = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: labelsKey });

/** The labels by id (a Case lists only ids; the list is read once). */
export function useLabelsById(): ReadonlyMap<string, Label> {
  const { data } = useLabels();
  return useMemo(() => new Map((data ?? []).map((l) => [l.id, l])), [data]);
}

/**
 * The labels of `ids`, in the person's order, those not known left out. A Me
 * that does not send `labelIds` yet (an older Vault) shows none.
 */
export function useLabelsOf(ids: readonly string[] | undefined): Label[] {
  const byId = useLabelsById();
  return (ids ?? []).flatMap((id) => byId.get(id) ?? []);
}

export function useCreateLabel() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; color?: string }) =>
      api<Label>("/api/me/labels", {
        method: "POST",
        body: { name: input.name, color: input.color },
        idempotencyKey: idempotencyKeyFor(input),
      }),
    ...optimisticMutation<Label[], { name: string; color?: string }>(
      qc,
      labelsKey,
      (old, input) =>
        old && [
          ...old,
          {
            id: newOptimisticId(),
            name: input.name,
            color:
              input.color ?? LABEL_COLORS[old.length % LABEL_COLORS.length],
            position: old.length,
          },
        ],
    ),
  });
}

export function useRenameLabel() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api<Label>(`/api/me/labels/${input.id}`, {
        method: "PATCH",
        body: { name: input.name },
      }),
    ...optimisticMutation<Label[], { id: string; name: string }>(
      qc,
      labelsKey,
      (old, input) =>
        old?.map((l) => (l.id === input.id ? { ...l, name: input.name } : l)),
    ),
  });
}

export function useRecolorLabel() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; color: string }) =>
      api<Label>(`/api/me/labels/${input.id}`, {
        method: "PATCH",
        body: { color: input.color },
      }),
    ...optimisticMutation<Label[], { id: string; color: string }>(
      qc,
      labelsKey,
      (old, input) =>
        old?.map((l) => (l.id === input.id ? { ...l, color: input.color } : l)),
    ),
  });
}

/** Puts the labels in the order of `ids` (the whole list). */
export function useReorderLabels() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[] }) =>
      api("/api/me/labels/order", { method: "PATCH", body: input }),
    ...optimisticMutation<Label[], { ids: string[] }>(
      qc,
      labelsKey,
      (old, input) => {
        if (!old) return old;
        const byId = new Map(old.map((l) => [l.id, l]));
        const ordered = input.ids.flatMap((id, position) => {
          const l = byId.get(id);
          return l ? [{ ...l, position }] : [];
        });
        return ordered.length === old.length ? ordered : old;
      },
    ),
  });
}

export function useDeleteLabel() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      api(`/api/me/labels/${input.id}`, { method: "DELETE" }),
    ...optimisticMutation<Label[], { id: string }>(
      qc,
      labelsKey,
      (old, input) => old?.filter((l) => l.id !== input.id),
    ),
  });
}
